"use client"

import { useEffect, useRef, useState, type CSSProperties } from "react"
import { GRAIN_URL, type GlassNavMode } from "@/components/public-nav-glass"
import { PageCanvas } from "@/components/page-canvas"
import { GLASS_DARK_BG, GLASS_LIGHT_BG } from "@/components/glass/tokens"

/**
 * The glass design system — the shared vocabulary behind the black-first
 * landing and every surface that continues it (currently /help).
 *
 * Everything is driven by the `--pg-*` custom properties below, which the
 * nav's sun/moon button flips between: dark is an all-black page built from
 * frosted panels, a fine grain overlay and very low-contrast gloss; light is
 * the same layout in cream.
 *
 * These lived inside landing-glass-client until /help needed the same
 * language. A second page copy-pasting the token table is how two surfaces
 * that are supposed to be one design start drifting apart.
 */

export const EASE = "cubic-bezier(0.22, 1, 0.36, 1)"

/**
 * Display headline typography — shared by /help, /blog, and any glass-surface
 * masthead. Sizes are inline (not `text-[clamp(...)]`) so a missed build
 * can't silently drop a headline to 16px.
 *
 * lineHeight stays above ~1.1 on purpose: at 1.05–1.06 the line box clips
 * descenders on g/y/p in our semibold grotesk, which reads as a flat-bottomed
 * glyph (the "cut-off G" bug). A hair of padding-bottom gives the tail room
 * without changing the visual density of the lockup.
 */
export const DISPLAY_SIZE = "clamp(2.3rem, 5.6vw, 4rem)"
export const DISPLAY_LEADING = 1.14

export function displayHeadingStyle(
  size: string = DISPLAY_SIZE,
  letterSpacing = "-0.04em",
): CSSProperties {
  return {
    fontSize: size,
    lineHeight: DISPLAY_LEADING,
    letterSpacing,
    paddingBottom: "0.06em",
  }
}

export const THEME: Record<GlassNavMode, Record<string, string>> = {
  dark: {
    "--pg-bg": GLASS_DARK_BG,
    "--pg-fg": "#F7F7F5",
    "--pg-muted": "rgba(247,247,245,0.52)",
    "--pg-faint": "rgba(247,247,245,0.32)",
    "--pg-line": "rgba(255,255,255,0.08)",
    // Was a 3.5% white wash over the page's near-black bg — through the
    // backdrop-blur that read as flat gray instead of the true black the
    // nav pill and footer use. A near-black tint gets the same color family
    // as those; kept translucent (not opaque) so the backdrop-blur still
    // does real glass work instead of reading as a solid dark box.
    "--pg-card": "rgba(0,0,0,0.4)",
    "--pg-card-edge": "rgba(255,255,255,0.09)",
    "--pg-gloss": "rgba(255,255,255,0.14)",
    "--pg-accent": "#F7F7F5",
    "--pg-invert-bg": "#FAFAF8",
    "--pg-invert-fg": "#08080A",
    "--pg-grain": "0.28",
    // The BOOTH mark's colour per theme, from the brand files
    // (booth-icon-gray.svg on dark, booth-icon-black.svg on light).
    "--pg-mark": "#6F6F6F",
    // CTA streak. Dark mode puts a near-WHITE primary button on a black page,
    // so its streak has to be dark to be seen at all; the glass secondary is
    // dark here, so its streak is light. They invert together in light mode.
    "--pg-sheen-solid": "rgba(8,8,10,0.20)",
    "--pg-sheen-glass": "rgba(255,255,255,0.34)",
  },
  light: {
    "--pg-bg": GLASS_LIGHT_BG,
    "--pg-fg": "#14120F",
    "--pg-muted": "rgba(20,18,15,0.58)",
    "--pg-faint": "rgba(20,18,15,0.38)",
    "--pg-line": "rgba(20,18,15,0.10)",
    "--pg-card": "rgba(255,255,255,0.55)",
    "--pg-card-edge": "rgba(255,255,255,0.85)",
    "--pg-gloss": "rgba(255,255,255,0.6)",
    "--pg-accent": "#14120F",
    "--pg-invert-bg": "#111010",
    "--pg-invert-fg": "#FAFAF8",
    "--pg-grain": "0.14",
    "--pg-mark": "#363636",
    "--pg-sheen-solid": "rgba(255,255,255,0.38)",
    "--pg-sheen-glass": "rgba(20,18,15,0.14)",
  },
}

// The glass panel used everywhere: frosted fill, hairline edge, and a
// diagonal specular sheen across the top-left corner.
export function GlassCard({ className = "", children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={`relative overflow-hidden rounded-[26px] backdrop-blur-2xl ${className}`}
      style={{
        background: "var(--pg-card)",
        border: "1px solid var(--pg-card-edge)",
        boxShadow: "inset 0 1px 0 var(--pg-gloss), 0 24px 60px -40px rgba(0,0,0,0.9)",
      }}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: "linear-gradient(158deg, var(--pg-gloss) 0%, transparent 44%)",
          opacity: 0.65,
        }}
      />
      {/* Soft round highlight blob, offset top-left — reads as light pooling
          on a liquid surface rather than a flat diagonal wash. */}
      <span
        aria-hidden
        className="pointer-events-none absolute -left-6 -top-10 h-40 w-40 rounded-full"
        style={{
          background: "radial-gradient(closest-side, var(--pg-gloss), transparent)",
          opacity: 0.5,
          filter: "blur(2px)",
        }}
      />
      <div className="relative">{children}</div>
    </div>
  )
}

// Fade-and-rise on first intersection. Unobserves immediately so sections
// don't re-animate on the way back up.
//
// staticBelowMd: below md the content is simply there — no fade, no rise.
// Done in CSS rather than a viewport hook, so the server-rendered page is
// already visible on a phone instead of waiting for hydration to decide.
export function Reveal({
  children,
  delay = 0,
  staticBelowMd = false,
}: {
  children: React.ReactNode
  delay?: number
  staticBelowMd?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === "undefined") {
      setShown(true)
      return
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true)
          io.disconnect()
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.05 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={staticBelowMd ? "reveal-static-sm" : undefined}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(18px)",
        transition: `opacity 700ms ease ${delay}ms, transform 900ms ${EASE} ${delay}ms`,
      }}
    >
      {children}
    </div>
  )
}

/* Section eyebrow. Deliberately NOT the letterspaced mono-caps label the
   rest of the marketing site uses — this surface wanted a thinner, quieter
   hierarchy, so it's a light sans label in sentence case led by a hairline
   rule. One component so every section stays on the same footing. */
export function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-3 text-[13px] font-light leading-none tracking-[-0.01em]"
      style={{ color: "var(--pg-faint)" }}
    >
      <span aria-hidden className="h-px w-7" style={{ background: "currentColor", opacity: 0.55 }} />
      {children}
    </span>
  )
}

/**
 * Page shell for a glass surface: paints the token table, lays the grain over
 * everything, and paints the mode's background on html/body — that is what
 * iOS 26 Safari tints its bars from (see components/page-canvas.tsx). A dark
 * page on the site-wide light root shows white bars top and bottom on iPhone.
 * Rendered here (not in a layout) so the canvas follows the light/dark toggle.
 */
export function GlassSurface({
  mode,
  className = "",
  children,
}: {
  mode: GlassNavMode
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      style={{
        ...(THEME[mode] as React.CSSProperties),
        background: "var(--pg-bg)",
        color: "var(--pg-fg)",
        transition: "background 700ms ease, color 700ms ease",
      }}
      className={`relative min-h-svh overflow-x-clip font-sans antialiased ${className}`}
    >
      <PageCanvas color={THEME[mode]["--pg-bg"]} scheme={mode} />
      {/* Grain is `absolute` over the whole surface, not `fixed inset-0`. iOS
          26 Safari samples any fixed element that touches the viewport edges
          to paint its bars, and a full-screen fixed overlay turned the bottom
          bar into a solid black slab that cut the page off — /search, which
          has no fixed overlay, got the see-through glass bar instead. The
          tile repeats, so covering the page instead of the viewport costs
          nothing visible. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[5] mix-blend-overlay"
        style={{ backgroundImage: GRAIN_URL, opacity: "var(--pg-grain)" }}
      />
      {children}
    </div>
  )
}

/** Respects the OS "reduce motion" switch — scroll-linked chrome turns off. */
export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
    const apply = () => setReduced(mq.matches)
    apply()
    mq.addEventListener("change", apply)
    return () => mq.removeEventListener("change", apply)
  }, [])
  return reduced
}

/** Segment pill — same glass segment control as the landing audience tabs. Always rounded-full. */
export function GlassSegmentControl<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  className = "",
}: {
  value: T
  onChange: (value: T) => void
  options: readonly { value: T; label: string }[]
  ariaLabel: string
  className?: string
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`inline-flex items-center gap-[3px] rounded-full p-[3px] backdrop-blur-xl ${className}`}
      style={{ background: "var(--pg-card)", border: "1px solid var(--pg-card-edge)" }}
    >
      {options.map((opt) => {
        const active = opt.value === value
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className="rounded-full px-3 py-1 text-[10px] font-medium transition-all duration-300 sm:px-3.5 sm:py-1.5 sm:text-[11px]"
            style={
              active
                ? { background: "var(--pg-invert-bg)", color: "var(--pg-invert-fg)" }
                : { background: "transparent", color: "var(--pg-muted)" }
            }
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}
